import React, { useState } from 'react';
import {
  IconButton, Badge, Menu, Typography, Box, Divider,
  Button, Chip, Tooltip, CircularProgress, useTheme,
  alpha
} from '@mui/material';
import {
  Notifications as NotificationIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  OpenInNew as ViewIcon,
  NotificationsNone as EmptyIcon,
  DoneAll as DoneAllIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { systemAlertsAPI, approvalsAPI } from '../../services/api';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

// Map entity types to frontend routes
const ENTITY_ROUTES = {
  invoice: '/sales-invoices',
  expense: '/expenses',
  purchase_order: '/purchase-orders',
  purchase_invoice: '/purchase-invoices',
  sales_order: '/sales-orders',
  credit_note: '/credit-notes',
  debit_note: '/debit-notes',
  budget: '/budgets',
  quotation: '/quotations',
  purchase_requisition: '/purchase-requisitions',
  stock_adjustment: '/inventory/stock-adjustments',
  stock_transfer: '/inventory/stock-transfers',
  trade_shipment: '/trade/shipments',
  letter_of_credit: '/trade/lcs',
  customs_declaration: '/trade/customs',
  freight_booking: '/trade/freight-bookings',
  container: '/trade/containers',
  journal_entry: '/accounting/journal-entries',
  leave_request: '/hr/leave-requests',
  inventory_item: '/inventory/item-list',
  item: '/inventory/item-list',
};

const ENTITY_LABELS = {
  invoice: 'Sales Invoice',
  expense: 'Expense',
  purchase_order: 'Purchase Order',
  purchase_invoice: 'Purchase Invoice',
  sales_order: 'Sales Order',
  credit_note: 'Credit Note',
  debit_note: 'Debit Note',
  budget: 'Budget',
  quotation: 'Quotation',
  purchase_requisition: 'Purchase Requisition',
  stock_adjustment: 'Stock Adjustment',
  stock_transfer: 'Stock Transfer',
  trade_shipment: 'Trade Shipment',
  letter_of_credit: 'Letter of Credit',
  customs_declaration: 'Customs Declaration',
  freight_booking: 'Freight Booking',
  container: 'Container',
  journal_entry: 'Journal Entry',
  leave_request: 'Leave Request',
  inventory_item: 'Inventory Item',
  item: 'Inventory Item',
};

const getDeepLinkPath = (entityType, entityId) => {
  const route = ENTITY_ROUTES[entityType];
  if (!route || !entityId) return null;
  return `${route}?highlight=${entityId}&mode=view`;
};

/**
 * Determines whether an approval alert should still show action buttons.
 * Hides Approve/Reject if:
 *   - The alert metadata carries approvalStatus that is already decided (approved/rejected/cancelled)
 *   - OR the alert itself was resolved/acknowledged
 */
const canActOnApproval = (alert) => {
  if (alert.source !== 'approval') return false;
  if (!alert.metadata?.approvalId) return false;
  // If the backend already set approvalStatus on the metadata, respect it
  const approvalStatus = alert.metadata?.approvalStatus;
  if (approvalStatus && approvalStatus !== 'pending') return false;
  // If the alert itself is resolved/acknowledged it shouldn't be shown at all,
  // but as a safety net also block actions here
  if (alert.status !== 'active') return false;
  return true;
};

const AlertNotificationCenter = () => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [actioning, setActioning] = useState({}); // { [alertId]: 'approve' | 'reject' }
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: alertsData, isLoading } = useQuery(
    ['systemAlerts', 'active'],
    () => systemAlertsAPI.getAll({ status: 'active', limit: 50 }),
    {
      enabled: !!user,
      refetchInterval: 30000,
      staleTime: 15000,
      refetchOnWindowFocus: true,
    }
  );

  const rawAlerts = alertsData?.data?.alerts || alertsData?.data?.items || alertsData?.alerts || [];

  // Only show truly active alerts. For approval alerts, additionally suppress those
  // whose approvalStatus is already decided (approved/rejected) even if the backend
  // hasn't resolved the system-alert record yet.
  const alerts = rawAlerts.filter(a => {
    if (a.status !== 'active') return false;
    if (a.source === 'approval') {
      const approvalStatus = a.metadata?.approvalStatus;
      if (approvalStatus && approvalStatus !== 'pending') return false;
    }
    return true;
  });

  // Sort: approval (pending action) alerts come first, then by recency
  const sorted = [...alerts].sort((a, b) => {
    const aIsApproval = canActOnApproval(a) ? -1 : 0;
    const bIsApproval = canActOnApproval(b) ? -1 : 0;
    if (aIsApproval !== bIsApproval) return aIsApproval - bIsApproval;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const activeCount = alerts.length;
  // Approval-action count shown as separate badge variant
  const pendingApprovalCount = alerts.filter(a => canActOnApproval(a)).length;

  const resolveMutation = useMutation(
    ({ id }) => systemAlertsAPI.resolve(id, 'Dismissed'),
    {
      onSuccess: () => queryClient.invalidateQueries(['systemAlerts']),
    }
  );

  const resolveAllMutation = useMutation(
    (ids) => systemAlertsAPI.resolveAll(ids, 'Dismissed all'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['systemAlerts']);
        enqueueSnackbar('All notifications marked as read', { variant: 'success' });
      },
      onError: (err) => {
        enqueueSnackbar(err.response?.data?.message || 'Failed to dismiss notifications', { variant: 'error' });
      }
    }
  );

  const handleDismissAll = () => {
    const alertIds = sorted.map(a => a.id);
    if (alertIds.length === 0) return;
    resolveAllMutation.mutate(alertIds);
  };

  const handleApproveFromAlert = async (alert) => {
    const approvalId = alert.metadata?.approvalId;
    if (!approvalId) return;
    setActioning(prev => ({ ...prev, [alert.id]: 'approve' }));
    try {
      await approvalsAPI.approve(approvalId, {});
      enqueueSnackbar('Approved successfully', { variant: 'success' });
      // Resolve the system alert immediately so it disappears
      resolveMutation.mutate({ id: alert.id });
      queryClient.invalidateQueries(['systemAlerts']);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Approval failed', { variant: 'error' });
    } finally {
      setActioning(prev => ({ ...prev, [alert.id]: null }));
    }
  };

  const handleRejectFromAlert = async (alert) => {
    const approvalId = alert.metadata?.approvalId;
    if (!approvalId) return;
    setActioning(prev => ({ ...prev, [alert.id]: 'reject' }));
    try {
      await approvalsAPI.reject(approvalId, { comments: 'Rejected via notification centre' });
      enqueueSnackbar('Rejected successfully', { variant: 'info' });
      resolveMutation.mutate({ id: alert.id });
      queryClient.invalidateQueries(['systemAlerts']);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Rejection failed', { variant: 'error' });
    } finally {
      setActioning(prev => ({ ...prev, [alert.id]: null }));
    }
  };

  const handleViewDetails = (alert) => {
    setAnchorEl(null);
    const meta = alert.metadata || {};
    const path = getDeepLinkPath(meta.entityType, meta.entityId);
    if (path) {
      navigate(path);
    } else {
      navigate('/workflow/approvals');
    }
  };

  const handleViewAll = () => {
    setAnchorEl(null);
    navigate('/notifications-approvals');
  };

  const getSeverityIcon = (severity, source) => {
    if (source === 'approval') return <WarningIcon color="warning" fontSize="small" />;
    switch (severity?.toLowerCase()) {
      case 'critical': return <ErrorIcon color="error" fontSize="small" />;
      case 'high':
      case 'warning': return <WarningIcon color="warning" fontSize="small" />;
      case 'info':
      default: return <InfoIcon color="info" fontSize="small" />;
    }
  };

  const getSeverityBg = (severity, source, isApprovalPending) => {
    if (isApprovalPending) return alpha(theme.palette.warning.main, 0.06);
    switch (severity?.toLowerCase()) {
      case 'critical': return alpha(theme.palette.error.main, 0.06);
      case 'high':
      case 'warning': return alpha(theme.palette.warning.main, 0.06);
      default: return 'transparent';
    }
  };

  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <Box>
      <Tooltip title={activeCount > 0 ? `${activeCount} notification${activeCount > 1 ? 's' : ''}` : 'No new notifications'}>
        <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <Badge badgeContent={activeCount || null} color={pendingApprovalCount > 0 ? 'warning' : 'error'}>
            <NotificationIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 580,
            borderRadius: 2,
            boxShadow: theme.shadows[12],
            display: 'flex',
            flexDirection: 'column',
          }
        }}
        transformOrigin={{ horizontal: theme.direction === 'rtl' ? 'left' : 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: theme.direction === 'rtl' ? 'left' : 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{
          px: 2, py: 1.5,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: '1px solid', borderColor: 'divider',
          flexShrink: 0,
        }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Notifications</Typography>
            {pendingApprovalCount > 0 && (
              <Typography variant="caption" color="warning.main" fontWeight={600}>
                {pendingApprovalCount} pending approval{pendingApprovalCount > 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {activeCount > 0 && (
              <Chip
                label={`${activeCount} new`}
                size="small"
                color={pendingApprovalCount > 0 ? 'warning' : 'error'}
                sx={{ fontWeight: 700, height: 22, fontSize: '0.72rem' }}
              />
            )}
            {activeCount > 0 && (
              <Tooltip title="Mark all notifications as read / dismiss">
                <Button
                  size="small"
                  variant="text"
                  disabled={resolveAllMutation.isLoading}
                  onClick={handleDismissAll}
                  startIcon={resolveAllMutation.isLoading ? <CircularProgress size={12} color="inherit" /> : <DoneAllIcon sx={{ fontSize: '1rem !important' }} />}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    py: 0.25,
                    px: 1,
                    minWidth: 'auto',
                    color: 'primary.main',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Mark all as read
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Notification list — scrollable */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {isLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : sorted.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <EmptyIcon sx={{ fontSize: 40, color: 'success.light', mb: 1, opacity: 0.4 }} />
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                All clear
              </Typography>
              <Typography variant="caption" color="text.disabled">
                No pending notifications or approvals
              </Typography>
            </Box>
          ) : (
            sorted.map((alert) => {
              const meta = alert.metadata || {};
              const isPendingApproval = canActOnApproval(alert);
              // Non-pending approval alerts (approved/rejected) — show as info, no action buttons
              const isCompletedApproval = alert.source === 'approval' && !isPendingApproval;
              const entityType = meta.entityType;
              const entityLabel = ENTITY_LABELS[entityType] || (entityType ? entityType.replace(/_/g, ' ') : '');
              const isActioning = actioning[alert.id];

              return (
                <Box
                  key={alert.id}
                  sx={{
                    px: 2, py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: getSeverityBg(alert.severity, alert.source, isPendingApproval),
                    '&:hover': { bgcolor: 'action.hover' },
                    transition: 'background-color 0.15s',
                  }}
                >
                  {/* Header row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {getSeverityIcon(alert.severity, alert.source)}
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color={isPendingApproval ? 'warning.main' : 'text.secondary'}
                      sx={{ textTransform: 'uppercase', flexGrow: 1, letterSpacing: '0.04em' }}
                    >
                      {isPendingApproval
                        ? `${entityLabel} — Awaiting Approval`
                        : isCompletedApproval
                          ? `${entityLabel} — ${(meta.approvalStatus || 'Processed').toUpperCase()}`
                          : alert.source === 'payment'
                            ? (meta.paymentType === 'receivable' ? 'Customer Payment Overdue' : 'Vendor Bill Overdue')
                            : alert.source === 'inventory'
                              ? (meta.stockStatus === 'out_of_stock' ? 'Out of Stock' : 'Low Stock Alert')
                              : (alert.type || alert.source || '').replace(/_/g, ' ')}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">{formatTime(alert.createdAt)}</Typography>
                  </Box>

                  {/* Title */}
                  <Typography variant="body2" fontWeight={600} gutterBottom sx={{ lineHeight: 1.4 }}>
                    {alert.title}
                  </Typography>

                  {/* Message */}
                  {alert.message && (
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {alert.message}
                    </Typography>
                  )}

                  {/* Action buttons */}
                  <Box sx={{ mt: 1, display: 'flex', gap: 0.75 }}>
                    {isPendingApproval ? (
                      <>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={isActioning === 'approve' ? <CircularProgress size={11} color="inherit" /> : <ApproveIcon />}
                          onClick={() => handleApproveFromAlert(alert)}
                          disabled={!!isActioning}
                          sx={{ textTransform: 'none', fontSize: '0.72rem', flex: 1 }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={isActioning === 'reject' ? <CircularProgress size={11} color="inherit" /> : <RejectIcon />}
                          onClick={() => handleRejectFromAlert(alert)}
                          disabled={!!isActioning}
                          sx={{ textTransform: 'none', fontSize: '0.72rem', flex: 1 }}
                        >
                          Reject
                        </Button>
                        <Tooltip title={`View ${entityLabel}`}>
                          <IconButton size="small" onClick={() => handleViewDetails(alert)} disabled={!!isActioning}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => resolveMutation.mutate({ id: alert.id })}
                          sx={{ textTransform: 'none', fontSize: '0.72rem', color: 'text.secondary' }}
                        >
                          Dismiss
                        </Button>
                        {(meta.entityType || meta.entityId) && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleViewDetails(alert)}
                            sx={{ textTransform: 'none', fontSize: '0.72rem', ml: 'auto' }}
                          >
                            View Details
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer */}
        <Box sx={{
          p: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1
        }}>
          <Button
            size="small"
            variant="text"
            onClick={handleViewAll}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'primary.main', flexGrow: 1, textAlign: 'left' }}
          >
            View All Notifications & Approvals
          </Button>
          {activeCount > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              disabled={resolveAllMutation.isLoading}
              onClick={handleDismissAll}
              startIcon={resolveAllMutation.isLoading ? <CircularProgress size={12} color="inherit" /> : <DoneAllIcon sx={{ fontSize: '0.9rem !important' }} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                borderColor: 'divider',
                whiteSpace: 'nowrap'
              }}
            >
              Dismiss All
            </Button>
          )}
        </Box>
      </Menu>
    </Box>
  );
};

export default AlertNotificationCenter;
